import FormGridItem from '~/components/FormComponents/FormGridItem.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import FormContainer from '~/modules/identification/components/FormComponents/FormContainer.tsx';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  EMPTY_ORGANIZATIONS,
  EMPTY_TAX_RESIDENCY_JURISDICTIONS,
  titleBlockOrganizations,
  tooltipBlockOrganizations,
} from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import { typeAppeals } from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion.tsx';
import { TaxResidencyJurisdictions } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/OrganizationSelfAssessmentDocumentForm/TaxResidencyJurisdictions/TaxResidencyJurisdictions.tsx';
import { Organizations } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/ControllingPersonSelfAssessmentDocumentForm/Organizations/Organizations.tsx';
import { styles as styles } from '~/modules/identification/styles.ts';
import { styles as stylesInfoIcon } from '~/components/FormComponents/styles.ts';
import { useEffect } from 'react';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { useBeneficiaryOwners } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/hooks/useBeneficiaryOwners.ts';
import { Bookmark } from '@mui/icons-material';

export const ControllingPersonSelfAssessmentDocumentForm = ({ index }: { index: number }) => {
  const { control, setValue } = useFormContext();
  const { beneficiaryOwners } = useBeneficiaryOwners();
  const basePath = `controllingPersons.${index}`;

  const [isTaxResidentOtherJurisdiction] = useWatch({
    name: [`${basePath}.isTaxResidentOtherJurisdiction`],
  });

  const {
    fields: fieldsTaxResidencies,
    append: appendTaxResidencies,
    remove: removeTaxResidencies,
  } = useFieldArray({
    control,
    name: `${basePath}.taxResidencies`,
  });
  const {
    fields: fieldsOrganizations,
    append: appendOrganizations,
    remove: removeOrganizations,
  } = useFieldArray({
    control,
    name: `${basePath}.organizations`,
  });

  if (!fieldsTaxResidencies.length) appendTaxResidencies(EMPTY_TAX_RESIDENCY_JURISDICTIONS);
  if (!fieldsOrganizations.length) appendOrganizations(EMPTY_ORGANIZATIONS);

  useEffect(() => {
    const owner = beneficiaryOwners[index];

    if (!owner) return;

    setValue(`${basePath}.id`, owner.id, { shouldDirty: false });
    setValue(`${basePath}.lastName`, owner.lastName, { shouldDirty: false });
    setValue(`${basePath}.firstName`, owner.firstName, { shouldDirty: false });
    setValue(`${basePath}.middleName`, owner.middleName, { shouldDirty: false });
  }, [beneficiaryOwners]);

  return (
    <Box sx={styles.border}>
      <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Typography variant="h6">Контролююча Особа {index + 1}</Typography>
        <Bookmark color="primary" sx={{ height: 30, width: 30 }} />
      </Grid>
      <Grid container spacing={3}>
        <FormGridItem>
          <FormInput
            name={`${basePath}.lastName`}
            label="Прізвище"
            placeholder="Введіть прізвище"
            disabled
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`${basePath}.firstName`}
            label="Ім'я"
            placeholder="Введіть ім'я"
            disabled
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`${basePath}.middleName`}
            label="По-батькові (за наявності)"
            placeholder="Введіть по-батькові (за наявності)"
            disabled
            fullWidth
          />
        </FormGridItem>
        <FormYesNoQuestion
          question={
            'Чи має КБВ статус податкового резидента іншої юрисдикції (країни) крім України та США?'
          }
          name={`${basePath}.isTaxResidentOtherJurisdiction`}
        />
        {isTaxResidentOtherJurisdiction && (
          <>
            <FormGridItem>
              <FormAutocomplete
                name={`${basePath}.salutation`}
                options={typeAppeals}
                inputProps={{
                  placeholder: 'Оберіть тип звернення',
                }}
                label="Звернення (наприклад, Mr, Dr, Ms, Herr тощо)"
                shouldSaveLabel
              />
            </FormGridItem>
            <Box sx={{ width: '100%' }}>
              <Typography variant="h5" sx={styles.titleForm}>
                Держава / юрисдикція резидентства для цілей оподаткування та відповідний номер
                платника податків
              </Typography>
            </Box>
            <Box sx={commonStyles.columnFlexContainer}>
              <FormContainer hasBookmark>
                {fieldsTaxResidencies.map((tax, taxIndex) => (
                  <TaxResidencyJurisdictions
                    key={tax.id}
                    index={taxIndex}
                    section={basePath}
                    append={appendTaxResidencies}
                    remove={removeTaxResidencies}
                  />
                ))}
              </FormContainer>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography variant="h6" sx={styles.titleForm}>
                {titleBlockOrganizations}
                <Tooltip title={tooltipBlockOrganizations} placement="right">
                  <InfoOutlineIcon sx={stylesInfoIcon.infoIcon} />
                </Tooltip>
              </Typography>
            </Box>
            <Box sx={commonStyles.columnFlexContainer}>
              <FormContainer hasBookmark>
                {fieldsOrganizations.map((organ, organIndex) => (
                  <Organizations
                    key={organ.id}
                    section={basePath}
                    index={organIndex}
                    append={appendOrganizations}
                    remove={removeOrganizations}
                  />
                ))}
              </FormContainer>
            </Box>
          </>
        )}
      </Grid>
    </Box>
  );
};
